import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Layers, ChevronLeft, ChevronRight } from "lucide-react";
import { API_URL } from "../config";

/** Полоса «Подборки» над списком материалов: тематические серии с обложками и числом видео. */
export default function SeriesStrip({ specialtyId, showEmpty = false, specialtyName }) {
  const [series, setSeries] = useState([]);
  const rowRef = useRef(null);
  const [edge, setEdge] = useState({ start: true, end: true });
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(1);
  const [loaded, setLoaded] = useState(false);

  const measure = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdge({ start: el.scrollLeft <= 2, end: el.scrollLeft >= max - 2 });
    const n = max <= 2 ? 1 : Math.ceil(el.scrollWidth / el.clientWidth);
    setPages(n);
    setPage(max <= 2 ? 0 : Math.min(n - 1, Math.round((el.scrollLeft / max) * (n - 1))));
  }, []);

  const go = dir => {
    const el = rowRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: "smooth" });
  };
  const goPage = i => {
    const el = rowRef.current;
    if (el) el.scrollTo({ left: (el.scrollWidth - el.clientWidth) * (pages > 1 ? i / (pages - 1) : 0), behavior: "smooth" });
  };

  useEffect(() => {
    const qs = specialtyId ? `?specialty=${encodeURIComponent(specialtyId)}` : "";
    fetch(`${API_URL}/series${qs}`)
      .then(r => (r.ok ? r.json() : []))
      .then(list => setSeries(Array.isArray(list) ? list.filter(x => x && x.id && x.title) : []))
      .catch(() => setSeries([]))
      .finally(() => setLoaded(true));
  }, [specialtyId]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [series, measure]);

  if (!series.length) {
    if (!showEmpty || !loaded) return null;
    return (
      <div className="series-strip series-empty" aria-label="Подборки материалов">
        <div className="series-strip-head">
          <div className="series-strip-title"><Layers size={16} strokeWidth={2.2} /> Подборки</div>
        </div>
        <p>
          Видеоматериалов {specialtyName ? `по направлению «${specialtyName}» ` : "по этой специальности "}пока нет.
          Если вы можете прочитать лекцию или разобрать случай, напишите нам: <a href="mailto:info@svrach.ru">info@svrach.ru</a>.
        </p>
      </div>
    );
  }
  return (
    <div className="series-strip" aria-label="Подборки материалов">
      <div className="series-strip-head">
        <div className="series-strip-title"><Layers size={16} strokeWidth={2.2} /> Подборки</div>
        {pages > 1 && (
          <div className="series-strip-arrows">
            <button type="button" aria-label="Назад" disabled={edge.start} onClick={() => go(-1)}><ChevronLeft size={18} /></button>
            <button type="button" aria-label="Вперёд" disabled={edge.end} onClick={() => go(1)}><ChevronRight size={18} /></button>
          </div>
        )}
      </div>
      <div className="series-strip-row" ref={rowRef} onScroll={measure}>
        {series.map(s => (
          <Link key={s.id} to={`/series/${s.id}`} className="series-card">
            <span className="series-card-cover">
              {(s.covers || []).length > 0
                ? s.covers.map((c, i) => <img key={i} src={c} alt="" loading="lazy" />)
                : <span className="lecture-thumb-fallback" />}
            </span>
            <span className="series-card-title">{s.title}</span>
            <span className="series-card-count">{s.count || 0} {plural(s.count || 0)}</span>
          </Link>
        ))}
      </div>
      {pages > 1 && (
        <div className="series-strip-dots" role="tablist" aria-label="Страницы подборок">
          {Array.from({ length: pages }, (_, i) => (
            <button key={i} type="button" role="tab" aria-selected={i === page} aria-label={`Страница ${i + 1}`}
              className={i === page ? "on" : ""} onClick={() => goPage(i)} />
          ))}
        </div>
      )}
    </div>
  );
}

export function plural(n) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "материал";
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return "материала";
  return "материалов";
}
